/**
 * First-touch UTM capture + handoff between the marketing site
 * (nestplot.com) and the app (app.nestplot.com). Pattern mirrors
 * AdPlus's `adplus_utm` localStorage bucket, adapted so it also
 * survives the marketing → app domain hop (localStorage is
 * per-origin, so we also encode the payload into the app-boot URL
 * query when the marketing site redirects into the signup wizard).
 *
 * When capture fires:
 *   - Any page load — MarketingShell calls `captureUtmFromLocation()`
 *     from a useEffect. Non-marketing URLs (organic + referrals)
 *     no-op harmlessly.
 *   - App-side load — the same helper reads `?_utm=<base64>` on the
 *     signup / verify page and rehydrates localStorage on the app
 *     origin so subsequent auth calls see the captured values.
 *
 * The captured object:
 *   { utm_source, utm_medium, utm_campaign, utm_content, utm_term,
 *     gclid, captured_at }
 * All optional; captured_at is an ISO 8601 timestamp used to expire
 * a stale bucket after 30 days (Google Ads' default attribution
 * window).
 */

const KEY = "nestplot_utm";
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** UTM keys we care about. Match the API schema exactly — nothing
 * else is forwarded to the server. */
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  // Additional ad click ids. wbraid/gbraid are Google's app/iOS click
  // ids (ride to the Google Ads offline-conversion upload with gclid);
  // fbclid is Meta's, used server-side to build the CAPI `fbc` value.
  "wbraid",
  "gbraid",
  "fbclid",
] as const;

export type UtmParams = Partial<Record<(typeof UTM_KEYS)[number], string>>;

type StoredUtm = UtmParams & { captured_at?: string };

/** Read the currently-stored UTM bundle, filtering out expired
 * entries. Returns {} when nothing is captured — callers treat that
 * as "organic traffic" and just don't send UTMs to the API. */
export function readUtm(): UtmParams {
  if (typeof window === "undefined") return {};
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(KEY);
  } catch {
    return {};
  }
  if (!raw) return {};
  let stored: StoredUtm;
  try {
    stored = JSON.parse(raw) as StoredUtm;
  } catch {
    return {};
  }
  if (stored.captured_at) {
    const captured = Date.parse(stored.captured_at);
    if (Number.isFinite(captured) && Date.now() - captured > MAX_AGE_MS) {
      // Expired — the CRM's attribution window is 30 days; anything
      // older is stale enough that the current visit should be
      // treated as organic.
      try {
        window.localStorage.removeItem(KEY);
      } catch { /* noop */ }
      return {};
    }
  }
  const out: UtmParams = {};
  for (const k of UTM_KEYS) {
    const v = stored[k];
    if (typeof v === "string" && v.length > 0) out[k] = v;
  }
  return out;
}

/** Write the UTM bundle to localStorage, replacing any prior. */
function writeUtm(payload: UtmParams): void {
  if (typeof window === "undefined") return;
  try {
    const stored: StoredUtm = { ...payload, captured_at: new Date().toISOString() };
    window.localStorage.setItem(KEY, JSON.stringify(stored));
  } catch { /* quota exceeded / private mode — ignore */ }
}

/** Parse `window.location.search`, extract UTM + gclid values, and
 * persist to localStorage if any are present. Idempotent — safe to
 * call on every route change (which MarketingShell does).
 *
 * Google Ads may append its own `gclid` on top of the ad's utm_*
 * params, so we honor "last value wins" when both are present
 * (Google's convention). */
export function captureUtmFromLocation(): UtmParams {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const captured: UtmParams = {};
  for (const k of UTM_KEYS) {
    const vals = params.getAll(k);
    if (vals.length > 0) captured[k] = vals[vals.length - 1];
  }
  // Also: some ad-server hosts append `_utm=<base64-json>` on the
  // marketing→app handoff (see `passUtmToUrl` below). Decode it and
  // treat it as a lower-priority source (URL query overrides the
  // handoff blob when both are present).
  const passthrough = params.get("_utm");
  if (passthrough) {
    try {
      const decoded = JSON.parse(atob(passthrough)) as UtmParams;
      for (const k of UTM_KEYS) {
        if (!captured[k] && typeof decoded[k] === "string" && decoded[k]) {
          captured[k] = decoded[k];
        }
      }
    } catch { /* invalid blob — ignore */ }
  }
  if (Object.keys(captured).length === 0) return readUtm();
  // Infer the source from a bare click id when the ad didn't also stamp
  // utm_source (common with auto-tagging). Keeps the Growth report from
  // bucketing paid clicks as "organic".
  if (!captured.utm_source) {
    if (captured.gclid || captured.wbraid || captured.gbraid) captured.utm_source = "google";
    else if (captured.fbclid) captured.utm_source = "meta";
  }
  writeUtm(captured);
  return captured;
}

/** Merge stored UTM values into the given API request body. Callers
 * do:
 *
 *   await api.auth.signup({ ...form, ...withUtm() })
 *
 * so the localStorage bundle rides along with every attribution-
 * eligible submission (signup, pilot-apply, inquiry, blog-subscribe).
 * Silently no-op when nothing is stored. */
export function withUtm<T extends Record<string, unknown>>(body: T): T & UtmParams {
  return { ...readUtm(), ...body };
}

/** Encode the stored UTMs into a `?_utm=<base64>` query-string blob
 * for cross-origin handoff (nestplot.com → app.nestplot.com). The
 * app-side `captureUtmFromLocation()` decodes it into its own
 * localStorage on first paint. Returns the input URL unchanged when
 * nothing is captured. */
export function passUtmToUrl(url: string): string {
  const utm = readUtm();
  if (Object.keys(utm).length === 0) return url;
  const encoded = btoa(JSON.stringify(utm));
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}_utm=${encodeURIComponent(encoded)}`;
}
